/**
 * 国别码选择组件 — 暗色主题
 * 使用 react-phone-number-input 获取全量国家数据
 * 显示国旗 emoji + 国家名称 + 区号
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input/input';
import { getCurLanuagePhone } from '@ivymobi/orbit-core/business/phoneNumber';
import { ChevronDown, Search } from 'lucide-react';

/** 将 ISO 3166-1 alpha-2 国家代码转为国旗 emoji */
function countryToFlag(isoCode: string): string {
  if (!isoCode || isoCode.length !== 2) return '';
  return isoCode
  .toUpperCase()
  .split('')
  .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
  .join('');
}

interface CountryCodeSelectProps {
  value: string; // 如 "+86"
  onChange: (code: string) => void;
}

export default function CountryCodeSelect({ value, onChange }: CountryCodeSelectProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // 获取多语言国家名称
  const labels = useMemo(() => getCurLanuagePhone(i18n), [i18n.language]);

  // 构建全量国家列表
  const countries = useMemo(() => {
    return getCountries().map((code) => ({
      code,
      callingCode: getCountryCallingCode(code),
      name: labels[code] || code,
      flag: countryToFlag(code),
    }));
  }, [labels]);

  // 当前选中
  const selected = useMemo(() => {
    const numericCode = value.replace('+', '');
    return countries.find((c) => c.callingCode === numericCode);
  }, [value, countries]);

  // 搜索过滤
  const filtered = useMemo(() => {
    if (!search.trim()) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.callingCode.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [countries, search]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (callingCode: string) => {
    onChange(`+${callingCode}`);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="tw-relative tw-flex-shrink-0" ref={menuRef}>
      {/* 触发按钮 */}
      <button
        type="button"
        className="tw-h-11 tw-px-2 tw-rounded-md tw-text-sm tw-flex tw-items-center tw-gap-1 tw-cursor-pointer tw-transition-colors"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          color: 'white',
          outline: 'none',
          minWidth: '88px',
        }}
        onClick={() => setOpen(!open)}
      >
        <span className="tw-text-base">{selected?.flag || '🌐'}</span>
        <span>{value}</span>
        <ChevronDown className="tw-h-3 tw-w-3 tw-opacity-60" />
      </button>

      {/* 下拉面板 */}
      {open && (
        <div
          className="tw-absolute tw-left-0 tw-top-full tw-mt-1 tw-z-50 tw-rounded-lg tw-overflow-hidden"
          style={{
            width: '280px',
            backgroundColor: '#1e293b',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          }}
        >
          {/* 搜索框 */}
          <div className="tw-px-2 tw-py-2" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="tw-relative">
              <Search className="tw-absolute tw-left-2 tw-top-1/2 tw--translate-y-1/2 tw-h-3.5 tw-w-3.5 tw-opacity-50" style={{ color: 'white' }} />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={i18n.language === 'zh-CN' ? '搜索国家或区号' : 'Search country or code'}
                className="tw-w-full tw-h-8 tw-pl-7 tw-pr-2 tw-rounded tw-text-xs"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  outline: 'none',
                }}
                onFocus={(e) => { e.target.style.borderColor = 'rgba(34, 211, 238, 0.5)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)'; }}
              />
            </div>
          </div>

          {/* 国家列表 */}
          <div className="tw-overflow-y-auto" style={{ maxHeight: '240px' }}>
            {filtered.map((country) => {
              const isSelected = value === `+${country.callingCode}`;
              return (
                <button
                  key={country.code}
                  type="button"
                  className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-left tw-transition-colors"
                  style={{
                    color: 'white',
                    backgroundColor: isSelected ? 'rgba(34, 211, 238, 0.1)' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => handleSelect(country.callingCode)}
                >
                  <span className="tw-text-base tw-w-6 tw-text-center">{country.flag}</span>
                  <span className="tw-flex-1 tw-truncate">{country.name}</span>
                  <span className="tw-text-xs tw-opacity-60 tw-shrink-0">+{country.callingCode}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="tw-py-4 tw-text-center tw-text-xs tw-opacity-50" style={{ color: 'white' }}>
                {i18n.language === 'zh-CN' ? '无匹配结果' : 'No results'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
